import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  min-height: 100vh;
  background: #0f0f23;
  color: #e2e8f0;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  max-width: 600px;
  margin: 3rem auto 4rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 1.1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  max-width: 960px;
  margin: 0 auto;
`;

const PlanCard = styled.div<{ featured?: boolean }>`
  background: #1a1a35;
  border: 2px solid ${p => p.featured ? '#7c3aed' : '#2d2d5e'};
  border-radius: 20px;
  padding: 2.5rem 2rem;
  position: relative;
  ${p => p.featured && `
    transform: scale(1.03);
    box-shadow: 0 0 40px rgba(124,58,237,0.3);
  `}
`;

const Badge = styled.span`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  color: white;
  padding: 0.3rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
`;

const PlanName = styled.h3`
  font-size: 1.3rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
`;

const PlanPrice = styled.div`
  font-size: 3rem;
  font-weight: 900;
  color: #7c3aed;
  margin: 1rem 0;
  span { font-size: 1rem; color: #94a3b8; font-weight: 400; }
`;

const PlanDuration = styled.p`
  color: #94a3b8;
  font-size: 0.9rem;
  margin: 0 0 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
`;

const Feature = styled.li`
  padding: 0.4rem 0;
  color: #cbd5e1;
  font-size: 0.95rem;
  &::before { content: '✓ '; color: #10b981; font-weight: 700; }
`;

const PayBtn = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

const Note = styled.p`
  text-align: center;
  color: #475569;
  font-size: 0.85rem;
  margin-top: 2rem;
`;

const PlansPage: React.FC = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const plans = [
    {
      name: 'Básico', price: 499, featured: false,
      features: [t('plans.feat_days_30'), t('plans.feat_photos_400'), t('plans.feat_qr'), t('plans.feat_zip')],
    },
    {
      name: 'Advanced', price: 899, featured: true,
      features: [t('plans.feat_days_60'), t('plans.feat_photos_800'), t('plans.feat_qr'), t('plans.feat_zip'), t('plans.feat_mod')],
    },
    {
      name: 'Pro', price: 1599, featured: false,
      features: [t('plans.feat_days_90'), t('plans.feat_photos_1500'), t('plans.feat_qr'), t('plans.feat_zip'), t('plans.feat_mod')],
    },
    {
      name: 'Platinum', price: 2699, featured: false,
      features: [t('plans.feat_days_120'), t('plans.feat_photos_2000'), t('plans.feat_qr'), t('plans.feat_zip'), t('plans.feat_mod')],
    },
  ];

  const handlePay = (plan: typeof plans[0]) => {
    alert(`PayPal integration coming soon.\nPlan: ${plan.name} — $${plan.price} MXN\n\nGo to the dashboard to create your event.`);
    if (admin) navigate('/dashboard');
    else navigate('/login');
  };

  return (
    <Wrapper>
      <Header>
        <Title>{t('plans.title')}</Title>
        <Subtitle>{t('plans.sub')}</Subtitle>
      </Header>

      <Grid>
        {plans.map(plan => (
          <PlanCard key={plan.name} featured={plan.featured}>
            {plan.featured && <Badge>{t('plans.popular')}</Badge>}
            <PlanName>{plan.name}</PlanName>
            <PlanPrice>${plan.price} <span>MXN</span></PlanPrice>
            <PlanDuration>{t('plans.duration')}</PlanDuration>
            <FeatureList>
              {plan.features.map(f => <Feature key={f}>{f}</Feature>)}
            </FeatureList>
            <PayBtn onClick={() => handlePay(plan)}>
              {t('plans.btn_pay')}
            </PayBtn>
          </PlanCard>
        ))}
      </Grid>

      <Note>
        {t('plans.note')} <Link to="/dashboard" style={{ color: '#7c3aed' }}>{t('plans.note_link')}</Link>
      </Note>
    </Wrapper>
  );
};

export default PlansPage;
